import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from '../lib/analytics';
import { api, type AppSettings } from '../lib/api';
import { clearState, currentJourney, loadState, saveState } from '../lib/journey';
import { Checking, Result } from './screens/Result';
import { Booking, Done, Secure } from './screens/Secure';
import { Check, Landing, Role, You, type ScreenProps } from './screens/Steps';
import { Shell } from './Shell';
import { FLOW_KEY, initialFlow, STEP_SCREENS, type Flow, type Screen } from './state';

function useWide(): boolean {
  const query = '(min-width: 900px)';
  const [wide, setWide] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setWide(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);
  return wide;
}

const BACK: Partial<Record<Screen, Screen>> = { role: 'landing', you: 'role', check: 'you', result: 'check', secure: 'result' };

export function App() {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [flow, setFlow] = useState<Flow>(() => {
    const stored = loadState<Flow>(FLOW_KEY);
    if (!stored) return initialFlow();
    // A refresh during the reveal restarts the reveal from the checklist.
    return stored.screen === 'checking' ? { ...stored, screen: 'check' } : stored;
  });
  const wide = useWide();
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const lastScreen = useRef<Screen>(flow.screen);

  useEffect(() => {
    currentJourney();
    api
      .settings()
      .then(setSettings)
      .catch(() => setLoadError(true));
    track('landing_viewed');
  }, []);

  useEffect(() => {
    saveState(FLOW_KEY, flow);
  }, [flow]);

  const update = useCallback((patch: Partial<Flow>) => setFlow((current) => ({ ...current, ...patch })), []);
  // Entering the reveal always re-evaluates: answers may have changed since the last result.
  const go = useCallback((screen: Screen) => setFlow((current) => ({ ...current, screen, ...(screen === 'checking' ? { evaluation: null } : {}) })), []);

  useEffect(() => {
    if (lastScreen.current === flow.screen) return;
    const previous = lastScreen.current;
    lastScreen.current = flow.screen;
    headingRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
    document.querySelector('.main')?.scrollTo({ top: 0 });
    if (previous === 'landing' && flow.screen === 'role') track('check_started', { step: 'role' });
    if (STEP_SCREENS.includes(previous) && previous !== flow.screen) track('step_completed', { step: previous as 'role' | 'you' | 'check' });
    if (flow.screen === 'result' && flow.evaluation) track('result_viewed', { step: 'result', result: flow.evaluation.view.result });
  }, [flow.screen, flow.evaluation]);

  const restart = useCallback(() => {
    clearState(FLOW_KEY);
    setFlow({ ...initialFlow(), screen: 'landing' });
  }, []);

  if (loadError) {
    return (
      <div className="page" style={{ alignItems: 'center', padding: 24 }}>
        <p className="s15" role="alert">
          Something went wrong loading the eligibility check. Refresh the page to try again.
        </p>
      </div>
    );
  }
  if (!settings) return <div className="page" aria-busy="true" />;

  const props: ScreenProps = { flow, settings, wide, update, go, headingRef };
  const locked = flow.submission != null;
  const back = BACK[flow.screen];
  const onBack = back && !(locked && flow.screen === 'secure') ? () => go(back) : null;

  return (
    <Shell screen={flow.screen} wide={wide} onBack={onBack}>
      {flow.screen === 'landing' ? <Landing {...props} /> : null}
      {flow.screen === 'role' ? <Role {...props} /> : null}
      {flow.screen === 'you' ? <You {...props} /> : null}
      {flow.screen === 'check' ? <Check {...props} /> : null}
      {flow.screen === 'checking' ? <Checking key="checking" {...props} /> : null}
      {flow.screen === 'result' ? <Result {...props} /> : null}
      {flow.screen === 'secure' ? <Secure {...props} /> : null}
      {flow.screen === 'booking' ? <Booking {...props} /> : null}
      {flow.screen === 'done' ? <Done {...props} onRestart={restart} update={(patch) => (patch.screen === 'landing' ? restart() : update(patch))} /> : null}
    </Shell>
  );
}
